import React, { createContext, useState, useContext, ReactNode, useMemo } from 'react';
import { BundleOption } from '../../../api/shopify/types';
import { ScoredAlgoliaHit } from '@jbhi-fi/apis/src/algolia/ProductClient';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { useSuggestedProducts } from '@jbhi-fi/components/src/hooks/useSuggestedProducts';
import { getAnalyticsTags } from '../../../utils/algolia/search-insights';
import { AlgoliaProductHit } from '../../../utils/types';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { fetchCurrentBundleOffering } from '../../../snippets/fetchCurrentBundleOffering';
import { SmartModuleSettings } from '../../../../global';

export type SmartModuleData = {
  frequentlyBoughtTogetherAlgoliaData?: Array<ScoredAlgoliaHit>;
  productBundleAlgoliaData?: Array<ScoredAlgoliaHit>;
  recentlyViewedAlgoliaData?: Array<ScoredAlgoliaHit>;
  recommendedForYouAlgoliaData?: Array<ScoredAlgoliaHit>;
};

const filterAvailableBundleItems = (products: Array<AlgoliaProductHit>) => {
  if (products?.length) {
    return products.filter(product => product.button === 'Buy' || product.button === 'PreOrder');
  }
  return [];
};

type SmartModuleContextOptions = {
  googleAcceptedDeviationPercentage: number;
  smartModuleSettings?: Array<SmartModuleSettings>;
  isEnhancedGoogleRecommendationsEnabled: boolean;
};

type SmartModuleContextProps = {
  smartModulesData: SmartModuleData;
  isLoading: boolean;
  setSmartModuleProductSku: (sku: string) => void;
};

type SmartModuleProviderProps = {
  currentProduct?: {
    sku: string;
    productGroup: string | number;
    optimizelyBundleOptions: Array<BundleOption>;
    bundleOptions: Array<BundleOption>;
  };
  setSmartModuleProductSku?: (sku: string) => void;
  options?: SmartModuleContextOptions;
  children: ReactNode;
};

const SmartModuleContext = createContext<SmartModuleContextProps | undefined>(undefined);

const SmartModuleProvider = ({ currentProduct, options, children }: SmartModuleProviderProps): JSX.Element => {
  const [productSku, setSmartModuleProductSku] = useState<string | undefined>(currentProduct?.sku);
  const { cart } = useCartContext();

  const hasCartLoaded = !!cart;
  const excludedSkus = useMemo(() => {
    return cart?.items.map(item => item.sku) ?? [];
  }, [hasCartLoaded]);

  const smartModuleSettings = options?.smartModuleSettings;
  const frequentlyBoughtTogetherSettings = smartModuleSettings?.find(
    option => option?.type === 'frequentlyBoughtTogether'
  );
  const recentlyViewedSettings = smartModuleSettings?.find(option => option?.type === 'recentlyViewed');
  const recommendedForYouSettings = smartModuleSettings?.find(option => option?.type === 'recommendedForYou');

  const { products: productsData, isLoading: isProductDataLoading } = useProducts({
    productSkus: [productSku || ''],
    options: { shouldResolveExtraOnlineProductFields: true },
    isEnabled: !!productSku,
  });

  const { products: recentlyViewedProducts, isLoading: isRecentlyViewedLoading } = useProducts({
    options: { shouldResolveExtraOnlineProductFields: true },
    isRecentlyViewed: recentlyViewedSettings?.isEnabled,
    isEnabled: recentlyViewedSettings?.isEnabled,
  });

  const currentProductAlgoliaData = productsData?.find(p => p.sku === productSku);

  /**
   * PRODUCT BUNDLE DATA
   */
  let bundleSuggestion: BundleOption | null = null;
  if (currentProduct?.sku) {
    bundleSuggestion = fetchCurrentBundleOffering(
      currentProduct.sku,
      currentProduct.productGroup,
      currentProduct.optimizelyBundleOptions,
      currentProduct.bundleOptions
    );
  }

  const { products: bundleSuggestions, isLoading: isBundleSuggestionLoading } = useProducts({
    productSkus: [productSku || '', ...(bundleSuggestion?.suggestedSkus.toString().split(',') ?? [])],
    options: { shouldResolveExtraOnlineProductFields: true },
    isEnabled: !!bundleSuggestion,
  });

  const { suggestions: frequentlyBoughtTogetherSuggestions, isLoading: isFrequentlyBoughtTogetherProductsLoading } =
    useSuggestedProducts({
      product: currentProductAlgoliaData,
      googleRecNumProducts: frequentlyBoughtTogetherSettings?.productCount || 12,
      googleRecModel: frequentlyBoughtTogetherSettings?.googleRecModelType ?? 'FBT',
      googleRecAcceptedDeviationPercentage: options?.googleAcceptedDeviationPercentage,
      excludedSkus: excludedSkus,
      isEnabled: frequentlyBoughtTogetherSettings?.isEnabled,
      analyticsTags: getAnalyticsTags('recai'),
      isAttachSuggestionEnabled: false,
      shouldEnhanceSuggestions: options?.isEnhancedGoogleRecommendationsEnabled,
    });

  const { suggestions: recommendedForYouSuggestions, isLoading: isRecommendedForYouProductsLoading } =
    useSuggestedProducts({
      product: currentProductAlgoliaData,
      googleRecNumProducts: recommendedForYouSettings?.productCount || 12,
      googleRecModel: recommendedForYouSettings?.googleRecModelType ?? 'RFY',
      googleRecAcceptedDeviationPercentage: options?.googleAcceptedDeviationPercentage,
      excludedSkus: excludedSkus,
      isEnabled: recommendedForYouSettings?.isEnabled,
      analyticsTags: getAnalyticsTags('recai'),
      isAttachSuggestionEnabled: false,
      shouldEnhanceSuggestions: options?.isEnhancedGoogleRecommendationsEnabled,
    });

  const isLoading =
    isProductDataLoading ||
    isFrequentlyBoughtTogetherProductsLoading ||
    isRecommendedForYouProductsLoading ||
    isRecentlyViewedLoading ||
    isBundleSuggestionLoading;

  const smartModulesData = useMemo((): SmartModuleData => {
    return {
      productBundleAlgoliaData: filterAvailableBundleItems(bundleSuggestions || []),
      recentlyViewedAlgoliaData:
        recentlyViewedSettings?.isEnabled && recentlyViewedProducts
          ? recentlyViewedProducts.filter(p => p.sku !== productSku)
          : [],
      frequentlyBoughtTogetherAlgoliaData:
        frequentlyBoughtTogetherSettings?.isEnabled && frequentlyBoughtTogetherSuggestions
          ? frequentlyBoughtTogetherSuggestions
          : [],
      recommendedForYouAlgoliaData:
        recommendedForYouSettings?.isEnabled && recommendedForYouSuggestions ? recommendedForYouSuggestions : [],
    };
  }, [
    frequentlyBoughtTogetherSettings?.isEnabled,
    recommendedForYouSettings?.isEnabled,
    recentlyViewedSettings?.isEnabled,
    bundleSuggestions,
    recentlyViewedProducts,
    productSku,
    frequentlyBoughtTogetherSuggestions,
    recommendedForYouSuggestions,
  ]);

  return (
    <SmartModuleContext.Provider value={{ smartModulesData, isLoading, setSmartModuleProductSku }}>
      {children}
    </SmartModuleContext.Provider>
  );
};

const useSmartModuleContext = (): SmartModuleContextProps => {
  const context = useContext(SmartModuleContext);
  if (context === undefined) {
    throw new Error('useSmartModule must be used within a SmartModuleProvider');
  }

  return context;
};

export { SmartModuleProvider, useSmartModuleContext };
